import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Users, MapPin, AlertTriangle, Folder, X, ChevronRight } from 'lucide-react';
import { reportsList, personnelList, alertsList } from '../data/mockData';
import { BINDA_LIST, KATEGORI_LAPORAN } from '../data/binda';
interface GlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export function GlobalSearch({
  open,
  onOpenChange
}: GlobalSearchProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) {
      setQuery('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(true);
      }
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onOpenChange]);
  if (!open) return null;
  const lowerQuery = query.toLowerCase();
  const filteredReports = query ? reportsList.filter(r => r.id.toLowerCase().includes(lowerQuery) || r.title.toLowerCase().includes(lowerQuery) || r.region.toLowerCase().includes(lowerQuery)).slice(0, 3) : [];
  const filteredPersonnel = query ? personnelList.filter(p => p.id.toLowerCase().includes(lowerQuery) || p.name.toLowerCase().includes(lowerQuery) || p.role.toLowerCase().includes(lowerQuery)).slice(0, 3) : [];
  const filteredRegions = query ? BINDA_LIST.filter(b => b.kode.toLowerCase().includes(lowerQuery) || b.binda.toLowerCase().includes(lowerQuery) || b.provinsi.toLowerCase().includes(lowerQuery)).slice(0, 3) : [];
  const filteredAlerts = query ? alertsList.filter(a => a.id.toLowerCase().includes(lowerQuery) || a.desc.toLowerCase().includes(lowerQuery) || a.location.toLowerCase().includes(lowerQuery)).slice(0, 3) : [];
  const filteredCategories = query ? KATEGORI_LAPORAN.filter(c => c.toLowerCase().includes(lowerQuery)).slice(0, 3) : [];
  const hasResults = filteredReports.length > 0 || filteredPersonnel.length > 0 || filteredRegions.length > 0 || filteredAlerts.length > 0 || filteredCategories.length > 0;
  const handleNavigate = (path: string) => {
    navigate(path);
    onOpenChange(false);
  };
  return <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh] sm:pt-[15vh] px-4" data-id="element-262">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm transition-opacity" onClick={() => onOpenChange(false)} data-id="element-263" />

      {/* Search Modal */}
      <div className="relative w-full max-w-2xl bg-black/80 border border-border/50 rounded-xl shadow-[0_0_40px_rgba(0,0,0,0.7)] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200" data-id="element-264">
        {/* Input Area */}
        <div className="flex items-center px-4 py-3 border-b border-border/50 bg-black/40" data-id="element-265">
          <Search className="w-5 h-5 text-primary mr-3 shrink-0" data-id="element-266" />
          <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)} placeholder="Cari laporan, personel, wilayah, peringatan..." className="flex-1 bg-transparent border-none outline-none text-sm font-mono text-foreground placeholder:text-muted-foreground/50 h-8" data-id="element-267" />
          <div className="flex items-center gap-2 ml-3 shrink-0" data-id="element-268">
            <span className="text-[10px] font-mono text-muted-foreground bg-white/5 px-1.5 py-0.5 rounded border border-border/30" data-id="element-269">
              ESC
            </span>
            <button onClick={() => onOpenChange(false)} className="text-muted-foreground hover:text-foreground transition-colors p-1" data-id="element-270">
              <X className="w-4 h-4" data-id="element-271" />
            </button>
          </div>
        </div>

        {/* Results Area */}
        <div className="max-h-[60vh] overflow-y-auto custom-scrollbar p-2" data-id="element-272">
          {!query ? <div className="p-8 text-center text-muted-foreground" data-id="element-273">
              <Search className="w-8 h-8 mx-auto mb-3 opacity-20" data-id="element-274" />
              <p className="text-xs font-mono uppercase tracking-widest" data-id="element-275">
                Ketik untuk mencari di seluruh sistem
              </p>
            </div> : !hasResults ? <div className="p-8 text-center text-muted-foreground" data-id="element-276">
              <p className="text-xs font-mono uppercase tracking-widest" data-id="element-277">
                Tidak ada hasil untuk "{query}"
              </p>
            </div> : <div className="space-y-4 p-2" data-id="element-278">
              {/* Reports */}
              {filteredReports.length > 0 && <div data-id="element-279">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 px-2 flex items-center gap-2" data-id="element-280">
                    <FileText className="w-3 h-3" data-id="element-281" /> Laporan Intelijen
                  </div>
                  <div className="space-y-1" data-id="element-282">
                    {filteredReports.map(r => <button key={r.id} onClick={() => handleNavigate(`/reports/${r.id}`)} className="w-full flex items-center justify-between p-2 rounded hover:bg-primary/10 hover:text-primary transition-colors group text-left" data-id="element-283">
                        <div className="flex flex-col min-w-0" data-id="element-284">
                          <span className="text-xs font-bold truncate group-hover:text-primary transition-colors" data-id="element-285">
                            {r.title}
                          </span>
                          <span className="text-[10px] font-mono text-muted-foreground truncate" data-id="element-286">
                            {r.id} • {r.region}
                          </span>
                        </div>
                        <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" data-id="element-287" />
                      </button>)}
                  </div>
                </div>}

              {/* Alerts */}
              {filteredAlerts.length > 0 && <div data-id="element-288">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 px-2 flex items-center gap-2" data-id="element-289">
                    <AlertTriangle className="w-3 h-3" data-id="element-290" /> Peringatan Dini
                  </div>
                  <div className="space-y-1" data-id="element-291">
                    {filteredAlerts.map(a => <button key={a.id} onClick={() => handleNavigate(`/alerts/${a.id}`)} className="w-full flex items-center justify-between p-2 rounded hover:bg-destructive/10 hover:text-destructive transition-colors group text-left" data-id="element-292">
                        <div className="flex flex-col min-w-0" data-id="element-293">
                          <span className="text-xs font-bold truncate group-hover:text-destructive transition-colors" data-id="element-294">
                            {a.desc}
                          </span>
                          <span className="text-[10px] font-mono text-muted-foreground truncate" data-id="element-295">
                            {a.id} • {a.location}
                          </span>
                        </div>
                        <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" data-id="element-296" />
                      </button>)}
                  </div>
                </div>}

              {/* Personnel */}
              {filteredPersonnel.length > 0 && <div data-id="element-297">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 px-2 flex items-center gap-2" data-id="element-298">
                    <Users className="w-3 h-3" data-id="element-299" /> Personel
                  </div>
                  <div className="space-y-1" data-id="element-300">
                    {filteredPersonnel.map(p => <button key={p.id} onClick={() => handleNavigate(`/personnel/${p.id}`)} className="w-full flex items-center justify-between p-2 rounded hover:bg-white/10 transition-colors group text-left" data-id="element-301">
                        <div className="flex flex-col min-w-0" data-id="element-302">
                          <span className="text-xs font-bold truncate" data-id="element-303">
                            {p.name}
                          </span>
                          <span className="text-[10px] font-mono text-muted-foreground truncate" data-id="element-304">
                            {p.id} • {p.role}
                          </span>
                        </div>
                        <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" data-id="element-305" />
                      </button>)}
                  </div>
                </div>}

              {/* Regions */}
              {filteredRegions.length > 0 && <div data-id="element-306">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 px-2 flex items-center gap-2" data-id="element-307">
                    <MapPin className="w-3 h-3" data-id="element-308" /> Wilayah / BINDA
                  </div>
                  <div className="space-y-1" data-id="element-309">
                    {filteredRegions.map(b => <button key={b.kode} onClick={() => handleNavigate(`/regional/${b.kode}`)} className="w-full flex items-center justify-between p-2 rounded hover:bg-white/10 transition-colors group text-left" data-id="element-310">
                        <div className="flex flex-col min-w-0" data-id="element-311">
                          <span className="text-xs font-bold truncate" data-id="element-312">
                            {b.binda}
                          </span>
                          <span className="text-[10px] font-mono text-muted-foreground truncate" data-id="element-313">
                            {b.kode} • {b.provinsi}
                          </span>
                        </div>
                        <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" data-id="element-314" />
                      </button>)}
                  </div>
                </div>}

              {/* Categories */}
              {filteredCategories.length > 0 && <div data-id="element-315">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2 px-2 flex items-center gap-2" data-id="element-316">
                    <Folder className="w-3 h-3" data-id="element-317" /> Kategori Laporan
                  </div>
                  <div className="space-y-1" data-id="element-318">
                    {filteredCategories.map(c => <button key={c} onClick={() => handleNavigate(`/reports`)} className="w-full flex items-center justify-between p-2 rounded hover:bg-white/10 transition-colors group text-left" data-id="element-319">
                        <div className="flex flex-col min-w-0" data-id="element-320">
                          <span className="text-xs font-bold truncate" data-id="element-321">
                            {c}
                          </span>
                        </div>
                        <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" data-id="element-322" />
                      </button>)}
                  </div>
                </div>}
            </div>}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-border/50 bg-black/40 flex justify-between items-center" data-id="element-323">
          <div className="flex items-center gap-4 text-[10px] font-mono text-muted-foreground" data-id="element-324">
            <span className="flex items-center gap-1" data-id="element-325">
              <span className="bg-white/5 px-1 rounded border border-border/30" data-id="element-326">
                ↑
              </span>
              <span className="bg-white/5 px-1 rounded border border-border/30" data-id="element-327">
                ↓
              </span>
              Navigasi
            </span>
            <span className="flex items-center gap-1" data-id="element-328">
              <span className="bg-white/5 px-1 rounded border border-border/30" data-id="element-329">
                ↵
              </span>
              Pilih
            </span>
          </div>
          <span className="text-[10px] font-mono text-primary uppercase tracking-widest" data-id="element-330">
            Pencarian Global
          </span>
        </div>
      </div>
    </div>;
}